import { z } from "zod"

// Step 1: Destination Details
export const invoiceSchema = z.object({
  invoiceFile: z.instanceof(File).optional(),
})

export const destinationSchema = z.object({
  purposeOfTravel: z.string().min(1, "Purpose of travel is required"),
  destination: z.string().min(1, "Destination is required"),
  dateRange: z.object({
    start_date: z.string().optional(),
    end_date: z.string().optional(),
  }).refine((data) => {
    if (!data.start_date || !data.end_date) return true;
    const startDate = new Date(data.start_date);
    const endDate = new Date(data.end_date);
    return startDate <= endDate;
  }, {
    message: "End date can not be earlier than start date",
    path: ["end_date"],
  }),
});


// Product Schema
export const currencySchema = z.object({
  currency: z.object({
    value: z.string(),
    label: z.string(),
  }),
  amount: z.number().positive("Amount must be positive"),
  rate: z.number().optional(),
})

export const productSchema = z.object({
  productType: z.object({
    value: z.string(),
    label: z.string(),
    type: z.string().optional(),
  }),
  product: z.object({
    value: z.string(),
    label: z.string(),
    type: z.string().optional(),
  }).optional(),
  cardType: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .optional(),
  currencies: z.array(currencySchema).min(1, "At least one currency is required"),
  local_id: z.string()
})

// Step 2: Customer Details
export const customerSchema = z.object({
  customerId: z.string().optional(),
  mobileNumber: z.string().min(10, "Mobile number must be at least 10 digits"),
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  pan: z.string().optional(),
  products: z.array(productSchema).optional(),
})

// Step 3: Delivery Details
export const addressSchema = z.object({
  address1: z.string().optional(),
  address2: z.string().optional(),
  landmark: z.string().optional(),
  country: z.object({
    value: z.string(),
    label: z.string(),
  }),
  state: z.object({
    value: z.string(),
    label: z.string(),
  }),
  city: z.string().optional(),
  pincode: z.string().optional(),

})

export const deliverySchema = z
  .object({
    deliveryMode: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .refine((data) => data.value.trim() !== "", {
        message: "Delivery mode is required",
      }),
    deliveryId: z.string().optional(),
    trackingId: z.string().optional(),
    products: z
      .array(
        z.object({
          value: z.string(),
          label: z.string(),
        })
      )
      .min(1, "At least one product is required"),
    customers: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .refine((data) => data.value.trim() !== "", {
        message: "Customer ID is required",
      }),
    deliveryType: z.enum(["self", "courier", "pickup"]).optional(),
    locationInfo: z.string().optional(),
    addresses: z.array(addressSchema).optional(), // <-- optional by default
    local_id: z.array(z.string()).optional(),
    address_id: z.string().optional(), // <-- optional by default
    date: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Only validate addresses & address_id when deliveryMode is NOT pickup
    if (data.deliveryMode?.value !== "pickup") {
      if (!data.addresses || data.addresses.length === 0) {
        ctx.addIssue({
          path: ["addresses"],
          message: "At least one address is required",
          code: z.ZodIssueCode.custom,
        });
      }
      // if (!data.address_id || data.address_id.trim() === "") {
      //   ctx.addIssue({
      //     path: ["address_id"],
      //     message: "Address ID is required",
      //     code: z.ZodIssueCode.custom,
      //   });
      // }
    }
  });

export const kycSchema = z.object({
  // make it required
  pan: z.instanceof(File).optional(),
  passportFront: z.instanceof(File).optional(),
  passportBack: z.instanceof(File).optional(),
})

// Complete Form Schema
export const travelBookingSchema = z.object({
  invoice: invoiceSchema.optional(),
  destination: destinationSchema,
  customers: z.array(customerSchema).min(1, "At least one customer is required"),
  deliveries: z.preprocess((val) => val ?? [], z.array(deliverySchema).optional()),
  order_type: z.enum(["buy", 'sell']).optional(),
  kyc: kycSchema.optional(),
})

export type DestinationFormValues = z.infer<typeof destinationSchema>
export type CustomerFormValues = z.infer<typeof customerSchema>
export type ProductFormValues = z.infer<typeof productSchema>
export type DeliveryFormValues = z.infer<typeof deliverySchema>
export type TravelBookingFormValues = z.infer<typeof travelBookingSchema>
export type InvoiceFormValues = z.infer<typeof invoiceSchema>
export type KYCFormValues = z.infer<typeof kycSchema>
export type ProductValues = z.infer<typeof productSchema>